import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faLock } from '@fortawesome/pro-regular-svg-icons';
import './SentFromSelect.scss';

import Tooltip from '../../General/Tooltip';
import { SENT_FROM_CHOICES, SENT_FROM_UNDECIDED, SENT_FROM_LOCKED_TIP } from '../../../helpers/notification_registry_helpers';

const CHOICES = [SENT_FROM_UNDECIDED, ...SENT_FROM_CHOICES];
let nextListId = 0;

// Sent from as a select whose options explain themselves. A template in code decides it, so a
// templated entry shows the choice locked instead.
const SentFromSelect = ({ value, onChange, locked = false, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const outerRef = useRef(null);
  const listId = useRef(null);
  if (!listId.current) listId.current = `sent-from-options-${++nextListId}`;
  const selectedIndex = Math.max(
    0,
    CHOICES.findIndex(choice => choice.value === (value || ''))
  );
  const selected = CHOICES[selectedIndex];

  useEffect(() => {
    if (!open) return;
    const closeOutside = e => !outerRef.current?.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [open]);

  if (locked)
    return (
      <div className={cn('sent-from-select locked', { compact })}>
        <Tooltip message={SENT_FROM_LOCKED_TIP} position='top'>
          <div className='trigger' tabIndex={0} aria-disabled='true' aria-label={`Sent from: ${selected.label}. ${SENT_FROM_LOCKED_TIP}`}>
            <span className='trigger-label'>{selected.label}</span>
            <FontAwesomeIcon icon={faLock} className='trigger-icon' />
          </div>
        </Tooltip>
        <div className='from-template'>from the template</div>
      </div>
    );

  const openMenu = () => {
    setActiveIndex(selectedIndex);
    setOpen(true);
  };
  const choose = choice => {
    setOpen(false);
    if (choice.value !== (value || '')) onChange(choice.value);
  };
  const onKeyDown = e => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return openMenu();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((activeIndex + step + CHOICES.length) % CHOICES.length);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open ? choose(CHOICES[activeIndex]) : openMenu();
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className={cn('sent-from-select', { compact, open })} ref={outerRef}>
      <button
        type='button'
        className='trigger'
        role='combobox'
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-controls={listId.current}
        aria-activedescendant={open ? `${listId.current}-${activeIndex}` : undefined}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className={cn('trigger-label', { undecided: !selected.value })}>{selected.label}</span>
        <FontAwesomeIcon icon={faChevronDown} className='trigger-icon' />
      </button>
      {open && (
        <ul className='menu' role='listbox' id={listId.current} aria-label='Sent from'>
          {CHOICES.map((choice, index) => (
            <li
              key={choice.value || 'undecided'}
              id={`${listId.current}-${index}`}
              role='option'
              aria-selected={index === selectedIndex}
              className={cn('option', { active: index === activeIndex })}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={e => e.preventDefault()}
              onClick={() => choose(choice)}
            >
              <div className='option-text'>
                <div className='option-label'>{choice.label}</div>
                <div className='option-description'>{choice.description}</div>
              </div>
              {index === selectedIndex && <FontAwesomeIcon icon={faCheck} className='option-check' />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

SentFromSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  locked: PropTypes.bool,
  compact: PropTypes.bool
};

export default SentFromSelect;
